import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useConfiguratorStore } from '@store/useConfiguratorStore';
import { useModelPreloader } from '@hooks/useModelPreloader';
import { defaultPrintAreas } from '@config/printAreas';
import { availableProducts } from '@config/products';
import { getDefaultModels } from '@utils/modelDiscovery';
import TopBar from '@components/UI/TopBar';
import FloatingToolbar from '@components/UI/FloatingToolbar';
import RightPanel from '@components/UI/RightPanel';
import BottomPanel from '@components/UI/BottomPanel';
import Scene from '@components/Scene/Scene';
import AppPreloader from '@components/AppPreloader';
import ErrorBoundary from '@components/ErrorBoundary';
import { ToastProvider } from '@components/UI/ToastProvider';
import './App.css';

function App() {
    const { productSlug } = useParams();
    const navigate = useNavigate();
    const { error, setPrintAreas, setCurrentModel, setGlbUrl } = useConfiguratorStore();
    const { isPreloading, progress } = useModelPreloader();

    useEffect(() => {
        // Initialize print areas on mount
        setPrintAreas(defaultPrintAreas);
    }, [setPrintAreas]);

    useEffect(() => {
        if (!productSlug) return;
        
        // Check if the product exists in our available configurations
        const isAvailableProduct = availableProducts.find(p => p.id === productSlug);
        const isDefaultModel = getDefaultModels().find(m => m.id === productSlug);
        
        const matchedModel = isAvailableProduct || isDefaultModel;
        
        if (matchedModel) {
            setCurrentModel(matchedModel.id);
            const glbPath = ('glbUrl' in matchedModel) ? matchedModel.glbUrl : matchedModel.glbPath;
            if (glbPath) {
                setGlbUrl(glbPath);
            }
        } else {
            // Invalid product, redirect to default
            navigate('/lip-balm', { replace: true });
        }
    }, [productSlug, navigate, setCurrentModel, setGlbUrl]);

    // Show full-screen preloader while loading all models
    if (isPreloading) {
        return <AppPreloader progress={progress} />;
    }

    return (
        <ToastProvider>
            <ErrorBoundary>
                <div className="app">
                    <TopBar />

                    {/* Main two-column layout */}
                    <div className="app-main">
                        {/* Left Panel: 3D View with floating controls */}
                        <div className="left-panel">
                            {error && (
                                <div className="error-overlay">
                                    <div className="error-message">{error}</div>
                                </div>
                            )}
                            <Scene />

                            {/* Floating toolbar for 3D controls */}
                            <FloatingToolbar />

                            {/* Color and Material controls overlay at bottom */}
                            <div className="bottom-overlay">
                                <BottomPanel />
                            </div>
                        </div>

                        {/* Right Panel: Print Area Editor */}
                        <div className="right-panel-container">
                            <RightPanel />
                        </div>
                    </div>
                </div>
            </ErrorBoundary>
        </ToastProvider>
    );
}

export default App;
